// agent-experience-redesign-v1.md §4.2 — auto-derive (gradient,
// glyph) for an agent from its domain template, with admin override
// via metadata_json.icon. Implements Q1 = B (auto + override).
//
// The IconTile gradient is picked from a per-domain map; the symbol
// inside is one of the curated NAME_MAP entries from Icon.jsx that
// appears in the IconPicker subset.

const TEMPLATE_TO_CATEGORY = {
  // Build family — purple/indigo/fuchsia mapping mirrors IconTile's
  // own category defaults so a Library tile ↔ Nav tab use the same
  // hue when the agent's domain is Build-flavored.
  legal_contracts:    "supervisor", // violet → indigo
  "legal-contracts":  "supervisor",
  legal:              "supervisor",
  it_helpdesk:        "agents",
  "it-helpdesk":      "agents",
  network_telecom:    "channels",
  "network-telecom":  "channels",
  healthcare:         "campaigns",
  finance:            "campaigns",
  "finance-accounting": "campaigns",
  finance_accounting: "campaigns",
  "supply-chain":     "schedules",
  supply_chain:       "schedules",
  hr:                 "marketplace",
  "hr-people-ops":    "marketplace",
  hr_people_ops:      "marketplace",
  sales:              "marketplace",
  "sales-crm":        "marketplace",
  sales_crm:          "marketplace",
  project_management: "supervisor",
  "project-management": "supervisor",
  engineering_devops: "builder",
  "engineering-devops": "builder",
  code:               "builder",
  generic:            "agents",
};

const TEMPLATE_TO_ICON_NAME = {
  legal_contracts:      "kb.book",
  "legal-contracts":    "kb.book",
  legal:                "kb.book",
  it_helpdesk:          "tab.admin",
  "it-helpdesk":        "tab.admin",
  network_telecom:      "node.api",
  "network-telecom":    "node.api",
  healthcare:           "feature.tester",
  finance:              "feature.spark",
  "finance-accounting": "feature.spark",
  finance_accounting:   "feature.spark",
  supply_chain:         "node.workflow",
  "supply-chain":       "node.workflow",
  hr:                   "user.profile",
  "hr-people-ops":      "user.profile",
  hr_people_ops:        "user.profile",
  sales:                "tab.campaigns",
  "sales-crm":          "tab.campaigns",
  sales_crm:            "tab.campaigns",
  project_management:   "feature.bookmark",
  "project-management": "feature.bookmark",
  engineering_devops:   "block.code",
  "engineering-devops": "block.code",
  code:                 "block.code",
  generic:              "tab.agents",
};

/**
 * Resolve (category, iconName) for an agent.
 *
 *   1. metadata_json.icon → admin override; takes precedence.
 *   2. agent.template     → domain map default.
 *   3. agent_kind          → supervisor / agents fallback.
 *   4. "tab.agents"        → ultimate generic fallback (Robot02).
 *
 * The category determines the IconTile gradient; the iconName is
 * the silhouette inside the squircle.
 */
export function resolveAgentIcon(agent) {
  if (!agent) return { category: "agents", iconName: "tab.agents" };

  // Admin override — metadata_json.icon stores either a NAME_MAP key
  // ("kb.book") or a structured override { category, iconName }.
  const meta = agent.metadata_json || {};
  if (typeof meta.icon === "string" && meta.icon.includes(".")) {
    // Bare iconName override; keep auto-derived category.
    const cat = resolveCategory(agent);
    return { category: meta.category || cat, iconName: meta.icon };
  }
  if (meta.icon && typeof meta.icon === "object") {
    const cat = meta.icon.category || resolveCategory(agent);
    const ic  = meta.icon.iconName || resolveIconName(agent);
    return { category: cat, iconName: ic };
  }

  return { category: resolveCategory(agent), iconName: resolveIconName(agent) };
}

function resolveCategory(agent) {
  const meta = agent.metadata_json || {};
  if (meta.category && typeof meta.category === "string") return meta.category;
  const t = agent.template;
  if (t && TEMPLATE_TO_CATEGORY[t]) return TEMPLATE_TO_CATEGORY[t];
  const kind = agent.agent_kind || meta.agent_kind || "regular";
  return kind === "supervisor" ? "supervisor" : "agents";
}

function resolveIconName(agent) {
  const t = agent.template;
  if (t && TEMPLATE_TO_ICON_NAME[t]) return TEMPLATE_TO_ICON_NAME[t];
  const kind = agent.agent_kind || agent.metadata_json?.agent_kind || "regular";
  if (kind === "supervisor") return "tab.supervisor";
  return "tab.agents";
}
